import { EmptyState, useMasterDetail } from '@common-components';
import AdsClickOutlined from '@mui/icons-material/AdsClickOutlined';
import ArrowBackOutlined from '@mui/icons-material/ArrowBackOutlined';
import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import PrintOutlined from '@mui/icons-material/PrintOutlined';
import SaveOutlined from '@mui/icons-material/SaveOutlined';
import Box from '@mui/material/Box';
import { typedMemo } from '@utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useBreadcrumbSegment } from '../breadcrumb/breadcrumbHooks';
import { UnsavedChangesDialog } from '../dialogs';
import { LoadingOverlay } from '../loading-overlay';
import { Toolbar, ToolbarItem } from '../toolbar';
import { DetailViewProps } from './types';

export const DetailView = typedMemo(
  <TFormData extends FieldValues>({
    idFieldName,
    customToolbarItems = [],
    hideSaveButton,
    isLoading,
    localizationKey,
    updateHook,
    createHook,
    deleteHook,
    children,
    identificationFields = [],
    templateHook,
  }: Readonly<DetailViewProps<TFormData>>) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { formState, handleSubmit, getValues, reset } =
      useFormContext<TFormData>();
    const { isValid, isDirty } = formState;
    const location = useLocation();
    const isNewRoute = location.pathname.endsWith('/new');
    const [showUnsavedChangesDialog, setShowUnsavedChangesDialog] =
      useState(false);
    const resolveCanSelectionChange = useRef<((value: boolean) => void) | null>(
      null,
    );
    const canChangeSelection = useCallback(() => {
      return new Promise<boolean>((resolve) => {
        if (isDirty) {
          resolveCanSelectionChange.current = resolve;
          setShowUnsavedChangesDialog(true);
        } else {
          resolve(true);
        }
      });
    }, [isDirty]);

    const onClose = useCallback(() => {
      canChangeSelection().then((canChange) => {
        if (canChange) {
          navigate('..');
        }
      });
    }, [navigate, canChangeSelection]);
    const masterDetailContext = useMasterDetail<TFormData>();

    const {
      selectedItem,
      updateItem,
      deleteItem,
      masterMinimized,
      toggleMasterMinimized,
      detailLoading,
    } = masterDetailContext ?? {};

    const shouldLoadTemplate = templateHook && isNewRoute;
    const templateResult = shouldLoadTemplate ? templateHook() : null;
    const templateLoading = templateResult?.loading ?? false;

    const updateMutation = updateHook(() => {
      if (updateItem && getValues()) {
        updateItem(getValues(), idFieldName);
        reset(getValues());
      }
    });

    const createMutation = createHook(() => {
      // After successful create, refetch template to get fresh data
      if (templateResult?.refetch) {
        templateResult.refetch();
      }
      // The form reset will happen in the effect below when new template data arrives
    });

    const deleteMutation = deleteHook(() => {
      if (deleteItem && getValues()) {
        const itemId = getValues()[idFieldName];
        deleteItem(itemId, idFieldName);
      }
    });

    const effectiveLoading =
      isLoading ||
      (detailLoading?.value ?? false) ||
      templateLoading ||
      updateMutation.loading ||
      createMutation.loading ||
      deleteMutation.loading;

    const { control } = useFormContext<TFormData>();
    useEffect(() => {
      if (control) {
        control._disableForm(effectiveLoading);
      }
    }, [effectiveLoading, control]);

    useEffect(() => {
      if (isNewRoute && templateResult?.result && !templateResult.loading) {
        reset(templateResult.result);
      }
    }, [isNewRoute, templateResult?.result, templateResult?.loading, reset]);

    const generateTitle = useCallback(() => {
      if (isNewRoute) {
        if (localizationKey) {
          const entityName = t(`${localizationKey}.captionSingular`);
          return `${entityName} - ${t('common.new')}`;
        }
        return t('common.new');
      }

      const values = getValues();
      if (identificationFields.length > 0) {
        const titleParts = identificationFields
          .map((field) => values[field])
          .filter((value) => value != null && value !== '');

        if (titleParts.length > 0) {
          return titleParts.join(' - ');
        }
      }
      if (localizationKey) {
        return t(`${localizationKey}.captionSingular`);
      }

      return 'Item';
    }, [isNewRoute, localizationKey, t, getValues, identificationFields]);

    // Determine if we're in a master-detail context or standalone
    const isInMasterDetailContext = masterDetailContext != null;

    const hasSelectedItem = masterDetailContext?.selectedItem.value != null;
    const showBreadcrumb = isNewRoute || hasSelectedItem;

    const breadcrumbId = isInMasterDetailContext
      ? 'detail-view'
      : 'standalone-detail';
    const breadcrumbOrder = isInMasterDetailContext ? 20 : 10;

    const { updateTitle } = useBreadcrumbSegment(
      breadcrumbId,
      showBreadcrumb ? generateTitle() : '',
      breadcrumbOrder,
    );

    useEffect(() => {
      updateTitle(showBreadcrumb ? generateTitle() : '');
    }, [showBreadcrumb, generateTitle, updateTitle]);

    const hasFormData = useCallback(() => {
      const values = getValues();
      return Object.keys(values).some((key) => {
        const value = values[key];
        return value !== undefined && value !== null && value !== '';
      });
    }, [getValues]);

    useEffect(() => {
      if (masterDetailContext) {
        masterDetailContext.canChangeSelection = canChangeSelection;
      }
    }, [masterDetailContext, canChangeSelection]);

    useEffect(() => {
      if (
        selectedItem?.value != null &&
        selectedItem.value[idFieldName] !== getValues(idFieldName)
      ) {
        reset(selectedItem.value);
      }
    }, [selectedItem, getValues, idFieldName, reset]);

    useEffect(() => {
      if (selectedItem?.value == null) {
        reset({} as TFormData);
      }
    }, [selectedItem, reset]);

    const onSave = useCallback(
      async (data: TFormData) => {
        console.log('Save Data:', data);
        if (isNewRoute) {
          createMutation.mutate(data);
        } else {
          updateMutation.mutate(data);
        }
      },
      [updateMutation, createMutation, isNewRoute],
    );

    const handleDialogSave = useCallback(() => {
      handleSubmit(async (data) => {
        await onSave(data);
        setShowUnsavedChangesDialog(false);
        resolveCanSelectionChange.current?.(true);
      })();
    }, [handleSubmit, onSave]);

    const handleDialogDiscard = useCallback(() => {
      setShowUnsavedChangesDialog(false);
      resolveCanSelectionChange.current?.(true);
    }, []);

    const handleDialogCancel = useCallback(() => {
      setShowUnsavedChangesDialog(false);
      resolveCanSelectionChange.current?.(false);
    }, []);

    const defaultToolbarItems = useMemo<ToolbarItem[]>(() => {
      const items: ToolbarItem[] = [];
      if (isInMasterDetailContext) {
        items.push({
          type: 'button',
          icon: masterMinimized?.value ? (
            <ArrowForwardOutlined />
          ) : (
            <ArrowBackOutlined />
          ),
          align: 'left',
          onClick: toggleMasterMinimized!,
        });
      }

      items.push({ type: 'title', title: generateTitle(), align: 'left' });

      if (!hideSaveButton) {
        items.push({
          type: 'button',
          label: t('common.save'),
          icon: <SaveOutlined />,
          variant: 'contained',
          align: 'right',
          disabled: !isValid || !isDirty || effectiveLoading,
          onClick: () => {
            console.log('Save clicked');
            handleSubmit(onSave)();
          },
        });
      }

      return items;
    }, [
      t,
      hideSaveButton,
      isValid,
      isDirty,
      effectiveLoading,
      handleSubmit,
      onSave,
      masterMinimized,
      toggleMasterMinimized,
      generateTitle,
      isInMasterDetailContext,
    ]);

    const moreOptionsChildren = useMemo<ToolbarItem[]>(
      () => [
        {
          type: 'button',
          label: t('common.delete'),
          icon: <DeleteOutlined />,
          onClick: () => {
            const itemId = getValues()[idFieldName];
            deleteMutation.mutate({ [idFieldName]: itemId });
          },
        },
        {
          type: 'button',
          label: t('detailView.printButton'),
          icon: <PrintOutlined />,
          onClick: () => console.log('Drucken clicked'),
        },
      ],
      [t, deleteMutation, idFieldName, getValues],
    );

    const allToolbarItems = useMemo(() => {
      const items = [...defaultToolbarItems, ...customToolbarItems];

      if (moreOptionsChildren.length > 0) {
        items.push({
          type: 'more-options',
          align: 'right',
          children: moreOptionsChildren,
        });
      }
      if (isNewRoute) {
        items.push({
          type: 'button',
          align: 'right',
          icon: <CloseOutlined />,
          onClick: onClose,
        });
      }
      return items;
    }, [
      defaultToolbarItems,
      customToolbarItems,
      moreOptionsChildren,
      isNewRoute,
      onClose,
    ]);

    if (!isNewRoute && !hasFormData()) {
      return (
        <Box position={'relative'} height="100%">
          {effectiveLoading && <LoadingOverlay top={48} />}
          <EmptyState
            icon={AdsClickOutlined}
            textKey={
              !isInMasterDetailContext
                ? 'detailView.noDataFound'
                : 'detailView.noItemSelected'
            }
          />
        </Box>
      );
    }
    return (
      <>
        <form style={{ height: '100%' }}>
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              backgroundColor: 'background.default',
              ...(masterDetailContext ? {} : { paddingInline: '0.5rem' }),
              position: 'relative',
            }}
          >
            <Toolbar items={allToolbarItems} />
            {effectiveLoading && <LoadingOverlay top={48} />}
            {children}
          </Box>
        </form>
        <UnsavedChangesDialog
          open={showUnsavedChangesDialog}
          onClose={handleDialogCancel}
          onSave={handleDialogSave}
          onDiscard={handleDialogDiscard}
          canSave={isValid}
        />
      </>
    );
  },
);
